<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="activities-page-title" :class="{'mb-1': getGuide('activity_list_page')}">
      <button class="btn btn-secondary-dark float-right"
              @click="handleFilterButton(false)"
              qid="activities-filter-button"
      >
        <font-awesome-icon icon="filter"/>
      </button>
      <button class="btn btn-secondary-dark float-right"
              @click="handleDownloadModal"
              qid="download-table-button"
              v-if="isGranted(permissions.MAINTENANCE_VIEW)"
      >
        <font-awesome-icon icon="file-download"/>
      </button>
      <router-link
          to="/activities/create"
          class="btn btn-success float-right ml-3"
          qid="create-s-button"
          v-if="isGranted(permissions.ACTIVITY_CREATE)"
      >
        <font-awesome-icon icon="plus-circle" /> {{ $t('activities.create_button') }}
      </router-link>
      <b-form-input
        type="text"
        v-model="filters.keywordValue"
        class="float-right mw-300 search-bar"
        qid="search-s-input"
        v-on:input="debounceInput"
        :placeholder="$t('activities.search_placeholder')"
      ></b-form-input>
      {{ $t('activities.title') }}
    </h4>
    <div>
      <span v-if="getGuide('activity_list_page')">
        <GuideButton :guide="getGuide('activity_list_page')"></GuideButton>
      </span>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="">
          <table class="table table-responsive-md table-list">
            <thead>
              <tr>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('name')"
                    qid="sort-s-name"
                >
                  {{ $t('activities.table.name') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                  </span>
                </th>
                <th scope="col" v-if="tableColumns.responsibles">
                  {{ $t('activities.table.owners') }}
                </th>
                <th scope="col" v-if="tableColumns.tags">
                  {{ $t('activities.table.tags') }}
                </th>
                <th scope="col" v-if="tableColumns.organizations">
                  {{ $t('activities.table.organizations') }}
                </th>
                <th scope="col" v-if="tableColumns.countries">
                  {{ $t('activities.table.countries') }}
                </th>
                <th scope="col" v-if="tableColumns.processGroup">
                  {{ $t('activities.table.process_groups') }}
                </th>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('updated_at')"
                    qid="sort-s-name"
                    width="160"
                >
                  {{ $t('system.last_updated') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                  </span>
                </th>
                <th width="140" class="text-right">
                  <b-nav-item-dropdown right extra-toggle-classes="no-dropdown-caret" class="guide-settings">
                    <!-- Using 'button-content' slot -->
                    <template slot="button-content">
                      <span class="float-right" style="list-style: none;">
                        <font-awesome-icon size="lg" icon="cog" class="text-muted"/>
                      </span>
                    </template>

                    <div class="pl-4 pr-4">
                      <b-form-checkbox
                        v-model="tableColumns.responsibles"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('processes.table.owners') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.tags"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('processes.table.tags') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.organizations"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('activities.table.organizations') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.countries"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('activities.table.countries') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.processGroup"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('activities.table.process_groups') }}
                      </b-form-checkbox>
                      <br>
                    </div>
                    <hr>
                    <div class="pl-4 pr-4">
                      <small class="text-muted">
                        * {{ $t('system.global_table_expand_explainer_text') }}
                      </small>
                    </div>
                  </b-nav-item-dropdown>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'activities.view', params: { slug: object.slug }}"
                      qid="s-view-page-link"
                  >
                    {{ object.name }}
                  </router-link>
                </td>
                <td v-if="tableColumns.responsibles">
                  <span class="mr-1 text-normal" v-for="user in getReducedUsers(object.users)">
                    <span qid="profile-photo">
                      <Avatar :object="user" size="xs"></Avatar>
                    </span>
                  </span>
                  <span v-if="object.users.length > 3"
                        class="badge cursor-pointer"
                        @click="openUsersModal(object)"
                  >
                    <font-awesome-icon icon="users"/>
                    + {{ object.users.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.tags">
                  <span v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                    <span class="badge" :title="tag.name_translated">
                      <font-awesome-icon icon="tag"/>
                      {{ tag.name_translated | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.tags_translated.length > 3"
                        class="badge cursor-pointer"
                        @click="openTagsModal(object)"
                  >
                    <font-awesome-icon icon="tags"/>
                    + {{ object.tags_translated.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.organizations">
                  <span class="mr-1" v-for="organization in getReducedOrganizations(object.organizations)" qid="p-organizations-list">
                    <span class="badge" :title="organization.name">
                      <font-awesome-icon icon="sitemap"/>
                      {{ organization.name | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.organizations.length > 1"
                        class="badge cursor-pointer"
                        @click="openOrganizationsModal(object)"
                  >
                    + {{ object.organizations.length - 1 }}
                  </span>
                </td>
                <td v-if="tableColumns.countries">
                  <span v-if="object.countries_with_flag && object.countries_with_flag.length">
                    <span class="mr-1" v-for="country in getReducedCountries(object.countries_with_flag)" qid="p-countries-list">
                      <span class="badge">
                        <img v-bind:src="country.flag" height="13" class="align-baseline mr-1" /> <span>{{country.name}}</span>
                      </span>
                    </span>
                    <span v-if="object.countries_with_flag && object.countries_with_flag.length && object.countries_with_flag.length > 1"
                          class="badge cursor-pointer"
                          @click="openCountriesModal(object)"
                    >
                      + {{ object.countries_with_flag.length - 1 }}
                    </span>
                  </span>
                </td>
                <td v-if="tableColumns.processGroup">
                  <span class="mr-1" v-for="group in getReducedProcessGroups(object.process_groups)" qid="p-countries-list">
                    <span class="badge" :title="group.name">
                      <font-awesome-icon icon="project-diagram"/>
                      {{ group.name | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.process_groups.length > 1"
                        class="badge cursor-pointer"
                        @click="openProcessGroupsModal(object)"
                  >
                    + {{ object.process_groups.length - 1 }}
                  </span>
                </td>
                <td>
                  <span class="text-normal">
                    {{ object.updated_at | formatDate }}
                  </span>
                </td>
                <td align="right" width="140">
                  <ActivityEditForm
                      :object="object"
                      @reloadContent="loadContent(currentPage)"
                      v-if="isGranted([permissions.ACTIVITY_EDIT, permissions.ACTIVITY_EDIT_OWN], object)"
                  ></ActivityEditForm>
                  <span class="btn btn-delete"
                        qid="delete-s-modal"
                        v-if="isGranted([permissions.ACTIVITY_DELETE, permissions.ACTIVITY_DELETE_OWN], object)"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group v-if="isMember()">
              <label qid="create-s-users-label">
                {{ $t('activities.create.owners_label') }}
              </label>
              <multiselect
                  v-model="filters.owners"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  :multiple="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('activities.create.owners_placeholder')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-owners"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="create-s-tags-label">
                {{ $t('activities.create.tags_label') }}
              </label>
              <multiselect
                  v-model="filters.tags"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="tags"
                  :multiple="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :taggable="true"
                  track-by="name_translated"
                  :placeholder="$t('activities.create.tags_placeholder')"
                  label="name_translated"
                  qid="filter-s-form-tags"
                  @search-change="loadTags"
                  @tag="addTag"
              ></multiselect>
            </b-form-group>
            <hr/>
            <b-form-group>
              <label qid="create-s-third-parties-label">
                {{ $t('activities.create.organizations_label') }}
              </label>
              <multiselect
                  v-model="filters.organizations"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="organizations"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :multiple="true"
                  :taggable="true"
                  track-by="name"
                  :placeholder="$t('activities.create.organizations_placeholder')"
                  label="name"
                  qid="filter-s-form-third-parties"
                  @search-change="loadOrganizations"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('processes.create.process_group_label') }}
              </label>
              <multiselect
                  v-model="filters.processGroup"
                  :options="processGroups"
                  :placeholder="$t('processes.create.process_group_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  @search-change="loadProcessGroups"
                  track-by="id"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('activities.processing_role.title') }}
              </label>
              <multiselect
                  v-model="filters.processingRole"
                  :options="processingRoles"
                  :placeholder="$t('activities.processing_role.pr_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <hr/>
            <b-form-group>
              <label qid="create-s-users-label">
                {{ $t('activities.create.modified_by_label') }}
              </label>
              <multiselect
                  v-model="filters.modifiedBy"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  :multiple="false"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('activities.create.modified_by_placeholder')"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-modified-by"
              >
                <template slot="singleLabel" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-from-label">
                {{ $t('activities.create.modified_by_date_from_label') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-from"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedFrom"
                  :clear-button="true"
                  :placeholder="$t('activities.create.modified_by_date_from_placeholder')"
              ></datepicker>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-to-label">
                {{ $t('activities.create.modified_by_date_to_label') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-to"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  :clear-button="true"
                  v-model="filters.modifiedTo"
                  :placeholder="$t('activities.create.modified_by_date_to_placeholder')"
              ></datepicker>
            </b-form-group>

            <a href="#" @click="resetFilters" class="text-right" qid="filter-s-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete confirm modal template -->
    <b-modal ref="delete-activity" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('activities.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('activities.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('activities.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
                  v-if="isGranted([permissions.ACTIVITY_DELETE, permissions.ACTIVITY_DELETE_OWN], itemToDelete)"
        >
          {{ $t('activities.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Download modal template -->
    <b-modal ref="download-modal" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('download_modal.title') }}
        </h4>
      </div>
      <div class="mt-2 mb-2">

        <label class="mt-2">
          {{ $t('download_modal.label.select') }}
        </label>

        <div class="row mt-2 mb-2">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.label.select_all') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="selectAll"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.basic_info"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.owners') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.owners"
              :options="radioOptions"
              qid="owners"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2">
          {{ $t('download_modal.value.data_mapping') }}
        </label>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.systems') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.systems"
              :options="radioOptions"
              qid="systems"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.system_data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.system_data"
              :options="radioOptions"
              qid="system_data"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_sources') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.third_party_sources"
              :options="radioOptions"
              qid="third_party_sources"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_sources_data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.third_party_sources_data"
              :options="radioOptions"
              qid="third_party_sources_data"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_receivers') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.third_party_receivers"
              :options="radioOptions"
              qid="third_party_receivers"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_receivers_data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.third_party_receivers_data"
              :options="radioOptions"
              qid="third_party_receivers_data"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.data_flow_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.data_flow_validation_log"
              :options="radioOptions"
              qid="data_flow_validation_log"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2">
          {{ $t('activities.measures.title') }}
        </label>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('activities.measures.title') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.measures"
              :options="radioOptions"
              qid="measures"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.gdprModule">
          {{ $t('download_modal.value.gdpr_module') }}
        </label>

        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.gdpr_basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.gdpr_basic_info"
              :options="radioOptions"
              qid="gdpr_basic_info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.data_subjects') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.data_subjects"
              :options="radioOptions"
              qid="data_subjects"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.retention_rules') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.retention_rules"
              :options="radioOptions"
              qid="retention_rules"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.retention_rule_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.retention_rule_validation_log"
              :options="radioOptions"
              qid="retention_rule_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.integrity_control_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.integrity_control_validation_log"
              :options="radioOptions"
              qid="integrity_control_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.processing_type_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.processing_type_validation_log"
              :options="radioOptions"
              qid="processing_type_validation_log"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.riskModule">
          {{ $t('download_modal.value.risk_control_module') }}
        </label>

        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_scenarios') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_scenarios"
              :options="radioOptions"
              qid="risk_scenarios"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_validation_log"
              :options="radioOptions"
              qid="risk_validation_log"
            ></b-form-radio-group>
          </div>
        </div>


      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="closeDownloadModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('activities.delete.cancel') }}
        </b-button>
        <b-button @click="downloadTable()"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
        >
          {{ $t('system.download') }}
        </b-button>
      </div>
    </b-modal>


    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.organizations"
        :show="showOrganizationsModal"
        @closeViewModal="closeOrganizationsModal"
        type="organizations"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.countries_with_flag"
        :show="showCountriesModal"
        @closeViewModal="closeCountriesModal"
        type="countries_with_flag"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.process_groups"
        :show="showProcessGroupsModal"
        @closeViewModal="closeProcessGroupsModal"
        type="process_groups"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import roleCheck from '@/util/ACL/roleCheck.js'
  import ProcessEditForm from '@/views/Processes/ProcessEditForm.vue';
  import {config} from '@/config.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import Datepicker from 'vuejs-datepicker';
  import ActivityEditForm from '@/views/Activities/ActivityEditForm.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Activities',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('activities.title'),
            active: true
          }
        ],
        selectAll: '',
        downloadForm: {
          basic_info: 1,
          owners: 0,
          systems: 0,
          system_data: 0,
          third_party_sources: 0,
          third_party_sources_data: 0,
          third_party_receivers: 0,
          third_party_receivers_data: 0,
          data_flow_validation_log: 0,
          gdpr_basic_info: 0,
          data_subjects: 0,
          retention_rules: 0,
          retention_rule_validation_log: 0,
          integrity_control_validation_log: 0,
          processing_type_validation_log: 0,
          risk_scenarios: 0,
          risk_validation_log: 0,
          measures: 0,
          client: {}
        },
        tableItems: [],
        itemToDelete: {},
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          processGroup: [],
          processingRole: [],
          owners: [],
          tags:[],
          modifiedBy: [],
          modifiedFrom: '',
          modifiedTo: '',
          organizations: [],
          keywordValue: ''
        },
        tableColumns: {
          responsibles: 1,
          tags: 1,
          organizations: 1,
          countries: 1,
          processGroup: 1,
        },
        processGroups: [],
        typeInput: '',
        loading: true,
        activeProcessGroups: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        tags: [],
        selectedTags: [],
        users: [],
        selectedUsers: [],
        newTags: [],
        organizations: [],
        selectedOrganization: [],
        processingRoles: [
          { text: this.$t('maintenance.third_parties.processing_role.data_controller'), value: 'Data controller'},
          { text: this.$t('maintenance.third_parties.processing_role.data_processor'), value: 'Data processor'},
          { text: this.$t('activities.processing_role.joint_controller_label'), value: 'Joint controller' }
        ],
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        selectedItem: {},
        showTagsModal: false,
        showOrganizationsModal: false,
        showCountriesModal: false,
        showProcessGroupsModal: false,
        showUsersModal: false,
        myGuides: []
      }
    },
    components: {
      ProcessEditForm,
      Avatar,
      Datepicker,
      ShowMoreModal,
      ActivityEditForm,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getTableColumns() {
        if (localStorage.getItem('tableColumns')) {
          return JSON.parse(localStorage.getItem('tableColumns'))
        } else {
          return {}
        }
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " of " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent()
        if (this.getTableColumns.activities) {
          this.tableColumns = this.getTableColumns.activities
        }
      },
      selectAll: function (value) {
        this.downloadForm.basic_info = value
        this.downloadForm.owners = value
        this.downloadForm.systems = value
        this.downloadForm.system_data = value

        if (this.getClient.contractBasicModule) {
          this.downloadForm.third_party_sources = value
          this.downloadForm.third_party_sources_data = value
          this.downloadForm.third_party_receivers = value
          this.downloadForm.third_party_receivers_data = value
        }

        this.downloadForm.data_flow_validation_log = value

        if (this.getClient.gdprModule) {
          this.downloadForm.gdpr_basic_info = value
          this.downloadForm.data_subjects = value
          this.downloadForm.retention_rules = value
          this.downloadForm.retention_rule_validation_log = value
          this.downloadForm.integrity_control_validation_log = value
          this.downloadForm.processing_type_validation_log = value
        }

        if (this.getClient.riskModule) {
          this.downloadForm.risk_scenarios = value
          this.downloadForm.risk_validation_log = value
        }

        this.downloadForm.measures = value
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      goToHome() {
        this.$router.push('/')
      },
      loadGuides(code) {

        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Activity',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.enabled
        });
        return existingGuide
      },
      openHelpCenter(object) {
        let topElement = document.getElementById("app");
        if (topElement.classList.contains("guide-collapsed")) {
          topElement.classList.toggle("guide-collapsed");
        }
        if (!topElement.classList.contains("activity-collapsed")) {
          topElement.classList.toggle("activity-collapsed");
        }

        this.$store.commit('storeHelpCenterState', true);
        this.$store.commit('storeActiveGuideScope', object);
      },
      resetFilters() {
        this.filters.owners = [];
        this.filters.tags = [];
        this.filters.modifiedBy = [];
        this.filters.modifiedFrom = '';
        this.filters.modifiedTo = '';
        this.filters.thirdParties = [];
        this.filters.organizations = [];
        this.filters.processingRole = [];
        this.filters.processGroup = [];
        this.debounceInput();
      },
      handleFilterButton(forceOpened) {
        if (!this.filterOpened) {
          this.loadUsers()
          this.loadTags()
          this.loadOrganizations()
          this.loadProcessGroups()
        }
        if (forceOpened) {
          this.filterOpened = true
        } else {
          this.filterOpened = !this.filterOpened
        }
      },
      handleDownloadModal() {
        this.$refs['download-modal'].show()
      },
      closeDownloadModal() {
        this.$refs['download-modal'].hide()
      },
      downloadTable() {
        let self = this;
        this.downloadForm.client = this.getClient.slug;
        piincHttp.post('activities/download', this.downloadForm).then(function() {
          self.closeDownloadModal();
          self.$toastr('success', self.$t('exports.success_message'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      openOrganizationsModal(object) {
        this.selectedItem = object
        this.showOrganizationsModal = true
      },
      closeOrganizationsModal() {
        this.selectedItem = {}
        this.showOrganizationsModal = false
      },
      openCountriesModal(object) {
        this.selectedItem = object
        this.showCountriesModal = true
      },
      closeCountriesModal() {
        this.selectedItem = {}
        this.showCountriesModal = false
      },
      openProcessGroupsModal(object) {
        this.selectedItem = object
        this.showProcessGroupsModal = true
      },
      closeProcessGroupsModal() {
        this.selectedItem = {}
        this.showProcessGroupsModal = false
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      getReducedOrganizations(organizations) {
        let reducedOrganizations = []
        if (organizations.length > 1) {
          reducedOrganizations = _.cloneDeep(organizations);
          reducedOrganizations = reducedOrganizations.slice(0, 1);
        } else {
          reducedOrganizations = organizations;
        }
        return reducedOrganizations;
      },
      getReducedCountries(countries) {
        let reducedCountries = []
        if (countries.length > 1) {
          reducedCountries = _.cloneDeep(countries);
          reducedCountries = reducedCountries.slice(0, 1);
        } else {
          reducedCountries = countries;
        }
        return reducedCountries;
      },
      getReducedProcessGroups(processGroups) {
        let reducedProcessGroups = []
        if (processGroups.length > 1) {
          reducedProcessGroups = _.cloneDeep(processGroups);
          reducedProcessGroups = reducedProcessGroups.slice(0, 1);
        } else {
          reducedProcessGroups = processGroups;
        }
        return reducedProcessGroups;
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      isMember() {
        if (this.getClient && this.getClient.user_roles && this.getClient.user_roles.length == 1 && this.getClient.user_roles[0].name == 'Member') {
          return false;
        } else {
          return true;
        }
      },
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      handleTypeInput(input) {
        this.typeInput = input;
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        let formOwners = [];
        let formTags = [];
        let formOrganizations = [];

        if (this.filters.owners && this.filters.owners.length) {
          for (var i = 0; i < this.filters.owners.length; i++) {
            formOwners[i] = this.filters.owners[i].id;
          }
          this.handleFilterButton(true)
        }

        if (this.filters.tags && this.filters.tags.length) {
          for (var j = 0; j < this.filters.tags.length; j++) {
            formTags[j] = this.filters.tags[j].id;
          }
          this.handleFilterButton(true)
        }

        if (this.filters.organizations && this.filters.organizations.length) {
          for (var t = 0; t < this.filters.organizations.length; t++) {
            formOrganizations[t] = this.filters.organizations[t].id;
          }
          this.handleFilterButton(true)
        }

        if (
          (this.filters.modifiedBy && this.filters.modifiedBy.id) ||
          (this.filters.processingRole && this.filters.processingRole.value) ||
          (this.filters.processGroup && this.filters.processGroup.id) ||
          this.filters.modifiedFrom ||
          this.filters.modifiedTo
        ) {
          this.handleFilterButton(true)
        }

        piincHttp.get('activities', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            owners: formOwners ? formOwners : undefined,
            tags: formTags ? formTags : undefined,
            organizations: formOrganizations ? formOrganizations : undefined,
            processing_role: this.filters.processingRole ? this.filters.processingRole.value : undefined,
            process_group: this.filters.processGroup ? this.filters.processGroup.id : undefined,
            modified_by: this.filters.modifiedBy ? this.filters.modifiedBy.id : undefined,
            modified_from: this.filters.modifiedFrom ? this.filters.modifiedFrom : undefined,
            modified_to: this.filters.modifiedTo ? this.filters.modifiedTo : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;

          if (!self.tableItems.length && roleCheck('Member')) {
            self.goToHome()
          }
        }, function() {});
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)

      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-activity'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-activity'].hide()
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('activities/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('activities.delete.success'))
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.tags = response.data
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
        }, function() {});
      },
      loadOrganizations(query) {
        let self = this;
        piincHttp.get('organizations', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.organizations = response.data.items
          if (self.organizations && !self.organizations.length && !query) {
            self.organizations.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadProcessGroups(query) {
        let self = this;
        piincHttp.get('process-groups', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.processGroups = response.data.items
          if (self.processGroups && !self.processGroups.length && !query) {
            self.processGroups.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'activities',
        filters: this.filters
      }
      let columnsObject = {
        type: 'activities',
        tableColumns: this.tableColumns
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storeTableColumns', columnsObject);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.activities) {
          this.filters = this.getFilters.activities
        }
        if (this.getTableColumns.activities) {
          this.tableColumns = this.getTableColumns.activities
        }
        this.loadContent()
      }
      this.loadGuides('activity_list_page')

    }
  }
</script>
